/**
 * The studio, built procedurally and pre-filtered into an IBL probe.
 *
 * A constant ambient term — which is what `mix(0.72, 1.0, visibility)` was —
 * cannot make a matte surface look photographed, because in a real room the
 * light that reaches a facing-up slope is nothing like the light that reaches a
 * facing-left one. So the ambient becomes an actual environment: a graded dome,
 * one large soft key box agreeing with the sun, a cool fill opposite it, a warm
 * bounce card off the table, and one small bright kicker whose job is the glint
 * along the plate's rim.
 *
 * Radiance values are linear and deliberately allowed above 1: the tone curve
 * downstream is what brings them back, and clamping here would flatten exactly
 * the highlights that read as a lamp.
 */

import * as THREE from "three";

/** Sky, horizon and floor radiance of the dome, in linear light. */
const SKY: readonly [number, number, number] = [0.5, 0.56, 0.66];
const HORIZON: readonly [number, number, number] = [0.42, 0.4, 0.4];
const FLOOR: readonly [number, number, number] = [0.3, 0.25, 0.21];

interface Card {
  /** Metres of the env dome; only the subtended angle matters. */
  readonly width: number;
  readonly height: number;
  readonly distance: number;
  /** Direction from the origin, in world space where +Z is up. */
  readonly direction: readonly [number, number, number];
  readonly radiance: readonly [number, number, number];
}

/**
 * The key follows the sun, so a highlight and the shadow it belongs to never
 * disagree. The fill, the bounce card and the kicker are fixtures of the room and
 * stay where they are.
 */
function cards(keyDirection: readonly [number, number, number]): ReadonlyArray<Card> {
  return [
    {
      width: 26,
      height: 19,
      distance: 24,
      direction: keyDirection,
      radiance: [7.4, 7.15, 6.7],
    },
    // Cool fill, opposite in azimuth and lower, so shadowed faces keep a hue
    // instead of going flat grey.
    {
      width: 22,
      height: 14,
      distance: 26,
      direction: [0.62, -0.55, 0.28],
      radiance: [0.72, 0.83, 1.02],
    },
    // Warm bounce off the table, which is what fills the undersides of spires.
    {
      width: 40,
      height: 40,
      distance: 16,
      direction: [0.05, 0.1, -1],
      radiance: [0.62, 0.5, 0.4],
    },
    // Small, hot, and the only source narrow enough to leave a crisp highlight on
    // the plate's rim.
    {
      width: 3.4,
      height: 3.4,
      distance: 30,
      direction: [-0.72, -0.18, 0.67],
      radiance: [34, 33, 31],
    },
  ];
}

const domeVertexShader = /* glsl */ `
varying vec3 vLocal;
void main() {
  vLocal = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const domeFragmentShader = /* glsl */ `
uniform vec3 uSky;
uniform vec3 uHorizon;
uniform vec3 uFloor;
varying vec3 vLocal;

void main() {
  float height = normalize(vLocal).z;
  vec3 radiance = height > 0.0
    ? mix(uHorizon, uSky, pow(height, 0.65))
    : mix(uHorizon, uFloor, pow(-height, 0.5));
  gl_FragColor = vec4(radiance, 1.0);
}
`;

/**
 * Pre-filtered environment for `scene.environment`, keyed to the sun's current
 * direction. It is cheap enough to rebuild whenever the sun moves — a couple of
 * small cube renders — which is what keeps the reflections agreeing with the
 * shadows instead of drifting apart as the light is dragged around.
 *
 * The caller owns the returned texture and must dispose it; everything built to
 * produce it is released here.
 */
export function buildStudioEnvironment(
  renderer: THREE.WebGLRenderer,
  keyDirection: readonly [number, number, number],
): THREE.Texture {
  const studio = new THREE.Scene();
  const disposableGeometries: THREE.BufferGeometry[] = [];
  const disposableMaterials: THREE.Material[] = [];

  const domeGeometry = new THREE.SphereGeometry(60, 40, 28);
  const domeMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uSky: { value: new THREE.Vector3(...SKY) },
      uHorizon: { value: new THREE.Vector3(...HORIZON) },
      uFloor: { value: new THREE.Vector3(...FLOOR) },
    },
    vertexShader: domeVertexShader,
    fragmentShader: domeFragmentShader,
    side: THREE.BackSide,
    depthWrite: false,
  });
  disposableGeometries.push(domeGeometry);
  disposableMaterials.push(domeMaterial);
  studio.add(new THREE.Mesh(domeGeometry, domeMaterial));

  for (const card of cards(keyDirection)) {
    const geometry = new THREE.PlaneGeometry(card.width, card.height);
    const material = new THREE.MeshBasicMaterial({ side: THREE.FrontSide });
    // Linear working space, so the radiance is stored verbatim rather than
    // being decoded from sRGB and clipped at 1.
    material.color.setRGB(
      card.radiance[0],
      card.radiance[1],
      card.radiance[2],
      THREE.LinearSRGBColorSpace,
    );
    disposableGeometries.push(geometry);
    disposableMaterials.push(material);

    const mesh = new THREE.Mesh(geometry, material);
    const direction = new THREE.Vector3(...card.direction).normalize();
    mesh.position.copy(direction).multiplyScalar(card.distance);
    // A plane's local +Z is its front face, so aiming it at the origin is what
    // turns the card towards the subject.
    mesh.lookAt(0, 0, 0);
    studio.add(mesh);
  }

  const generator = new THREE.PMREMGenerator(renderer);
  // A little pre-blur: the cards have hard edges, and a hard-edged reflection of
  // a softbox is a giveaway that the room is polygons.
  const target = generator.fromScene(studio, 0.035, 0.5, 200);
  generator.dispose();
  for (const geometry of disposableGeometries) geometry.dispose();
  for (const material of disposableMaterials) material.dispose();
  return target.texture;
}
